import { useState, useEffect, useCallback, useRef } from 'react';
import { useBusiness } from '../context/BusinessContext';
import api from '../utils/api';

/**
 * Hook to access subscription plan and features for the current business (supplier)
 */
export const useBusinessSubscription = () => {
  const { businessId } = useBusiness();
  const requestSeq = useRef(0);
  const [planKey, setPlanKey] = useState(null);
  const [features, setFeatures] = useState([]);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [aiLimit, setAiLimit] = useState(null);
  const [aiUsed, setAiUsed] = useState(0);
  const [aiRemaining, setAiRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPlanInfo = useCallback(async () => {
    const requestId = requestSeq.current + 1;
    requestSeq.current = requestId;

    if (!businessId) {
      setPlanKey(null);
      setFeatures([]);
      setIsTrial(false);
      setTrialEndsAt(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPlanKey(null);
      setFeatures([]);
      setIsTrial(false);
      setTrialEndsAt(null);
      const [planRes, creditsRes] = await Promise.all([
        api.get(`/api/business/${businessId}/subscriptions/plan-info`).catch(() => ({ data: { planKey: 'supplier_free', features: [], isTrial: false, trialEndsAt: null } })),
        api.get('/api/ai/credits').catch(() => ({ data: {} }))
      ]);

      if (requestId !== requestSeq.current) return;

      const planData = planRes.data;
      setPlanKey(planData.planKey || 'supplier_free');
      setFeatures(planData.features || []);
      setIsTrial(planData.isTrial || false);
      setTrialEndsAt(planData.trialEndsAt || null);

      const creditsData = creditsRes.data;
      setAiLimit(creditsData.messageLimit ?? null);
      setAiUsed(creditsData.messagesUsed ?? 0);
      setAiRemaining(creditsData.messagesRemaining ?? null);
    } catch (err) {
      if (requestId !== requestSeq.current) return;

      setError(err.response?.data?.message || 'Failed to load subscription');
      setPlanKey('supplier_free');
      setFeatures([]);
      setIsTrial(false);
      setTrialEndsAt(null);
    } finally {
      if (requestId === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [businessId]);

  useEffect(() => {
    loadPlanInfo();
  }, [loadPlanInfo]);

  const hasFeature = useCallback((featureKey) => {
    if (!features || features.length === 0) return false;
    const keys = features.map(f => (typeof f === 'string' ? f : f.key || f));
    if (keys.includes('all_features')) return true;
    return keys.includes(featureKey);
  }, [features]);

  const hasAnyFeature = useCallback((featureKeys) => {
    return featureKeys.some(key => hasFeature(key));
  }, [hasFeature]);

  const hasAllFeatures = useCallback((featureKeys) => {
    return featureKeys.every(key => hasFeature(key));
  }, [hasFeature]);

  return {
    planKey,
    features,
    isTrial,
    trialEndsAt,
    aiLimit,
    aiUsed,
    aiRemaining,
    loading,
    error,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    refresh: loadPlanInfo
  };
};
